import { createContext, type ReactNode } from 'react';
import { useUserPreferences, type UserPreferences } from '../hooks/useUserPreferences';

export interface PreferencesContextType {
  preferences: UserPreferences | undefined;
  isLoading: boolean;
}

// Context export required for useContext pattern - Fast Refresh warning is acceptable
// eslint-disable-next-line react-refresh/only-export-components
export const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

/**
 * Pulls the current user's preferences into a context so consumers like
 * useTableDensity re-render when the user updates their preferences.
 *
 * useUserPreferences gates its fetch on auth internally — this provider
 * can wrap public pages safely; the query simply stays disabled until
 * the user logs in. After a successful mutation,
 * useUpdateUserPreferences invalidates the same queryKey, which triggers
 * a refetch here, which updates every consumer (table density, theme,
 * etc.) without a hard reload.
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useUserPreferences();
  return (
    <PreferencesContext.Provider value={{ preferences: data, isLoading }}>
      {children}
    </PreferencesContext.Provider>
  );
}

// Re-export usePreferences hook for convenience
// eslint-disable-next-line react-refresh/only-export-components
export { usePreferences } from '../hooks/usePreferences';
